import { motion } from "framer-motion";
import { Target, TrendingUp, Award, Shield, ArrowRight } from "lucide-react";

export const PersonalTraining = () => {
  const scrollToContact = () => {
    const element = document.querySelector("#contacto");
    if (element) element.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="personal" className="py-20 lg:py-32 bg-[#FAF8F5]" data-testid="personal-training-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="text-center mb-16">
          <span className="text-[#FED601] text-sm font-bold uppercase tracking-widest">Resultados Premium</span>
          <h2 className="text-4xl lg:text-5xl font-black text-[#1A1A1A] mt-2 mb-4">Personal Training</h2>
          <p className="text-[#4A4A4A] mt-4 max-w-2xl mx-auto text-lg">
            Um profissional 100% dedicado a ti, focado nos teus resultados. Esta é a forma mais eficaz de atingir os teus objetivos.
          </p>
        </motion.div>

        {/* Guarantees - Puzzle / Bento Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-3 grid-rows-2 gap-3 lg:gap-4 mb-20 auto-rows-[140px] lg:auto-rows-[160px]">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
            className="col-span-2 row-span-1 lg:col-start-2 lg:col-span-2 lg:row-start-1 bg-[#1A1A1A] rounded-2xl p-6 lg:p-8 flex items-center gap-5 group hover:bg-[#222] transition-colors relative overflow-hidden"
            data-testid="guarantee-card-0">
            <div className="absolute -right-8 -bottom-8 w-32 h-32 bg-[#FED601]/5 rounded-full group-hover:scale-150 transition-transform duration-700" />
            <div className="w-14 h-14 bg-[#FED601] rounded-xl flex items-center justify-center flex-shrink-0 group-hover:rotate-6 transition-transform"><Target size={28} className="text-black" /></div>
            <div>
              <h3 className="text-lg lg:text-xl font-black text-white mb-1">100% Focado em Ti</h3>
              <p className="text-gray-400 text-sm">Treino completamente personalizado aos teus objetivos.</p>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.1 }}
            className="col-span-1 row-span-2 lg:col-start-1 lg:row-start-1 lg:row-span-2 bg-[#FED601] rounded-2xl p-6 lg:p-8 flex flex-col justify-between group hover:bg-[#E0BC00] transition-colors relative overflow-hidden"
            data-testid="guarantee-card-1">
            <div className="absolute -left-6 -top-6 w-24 h-24 bg-black/5 rounded-full" />
            <div className="w-12 h-12 bg-black/10 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform"><TrendingUp size={26} className="text-black" /></div>
            <div>
              <h3 className="text-lg lg:text-xl font-black text-black mb-1">Resultados Garantidos</h3>
              <p className="text-black/70 text-sm">Acompanhamento multidisciplinar contínuo com ajustes constantes ao teu programa. Alimentação, treino e repouso são os 3 pilares que sustentam a estratégia do nosso sucesso.</p>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.2 }}
            className="col-span-1 row-span-1 lg:col-start-2 lg:row-start-2 bg-white border-2 border-[#E0DDD7] rounded-2xl p-5 lg:p-6 flex flex-col justify-center group hover:border-[#FED601] transition-colors relative overflow-hidden"
            data-testid="guarantee-card-2">
            <div className="absolute -right-4 -top-4 w-16 h-16 bg-[#FED601]/10 rounded-full group-hover:scale-[2] transition-transform duration-500" />
            <div className="w-10 h-10 bg-[#1A1A1A] rounded-lg flex items-center justify-center mb-3 group-hover:bg-[#FED601] transition-colors"><Shield size={20} className="text-[#FED601] group-hover:text-black transition-colors" /></div>
            <h3 className="text-base font-bold text-[#1A1A1A]">Segurança Total</h3>
            <p className="text-[#4A4A4A] text-xs mt-1">Técnica perfeita e progressão adequada.</p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.15 }}
            className="col-span-1 row-span-1 lg:col-start-3 lg:row-start-2 bg-[#1A1A1A] rounded-2xl p-5 lg:p-6 flex flex-col justify-center group hover:bg-[#222] transition-colors relative overflow-hidden"
            data-testid="guarantee-card-3">
            <div className="absolute -left-4 -bottom-4 w-16 h-16 bg-[#FED601]/10 rounded-full group-hover:scale-[2] transition-transform duration-500" />
            <div className="w-10 h-10 bg-[#FED601] rounded-lg flex items-center justify-center mb-3 group-hover:rotate-6 transition-transform"><Award size={20} className="text-black" /></div>
            <h3 className="text-base font-bold text-white">Profissionais Certificados</h3>
            <p className="text-gray-400 text-xs mt-1">Personal trainers com formação especializada.</p>
          </motion.div>
        </div>

        {/* CTA */}
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="text-center">
          <div className="inline-block bg-[#1A1A1A] p-8 lg:p-12 rounded-lg">
            <h3 className="text-2xl lg:text-3xl font-bold text-white mb-4">Pronto Para a Tua Transformação?</h3>
            <p className="text-gray-400 mb-6 max-w-md mx-auto">Marca já a tua sessão experimental e descobre o que o personal training pode fazer por ti.</p>
            <button onClick={scrollToContact} className="bg-[#FED601] text-black px-8 py-4 text-lg font-bold uppercase tracking-wider hover:bg-[#E0BC00] transition-colors flex items-center gap-2 mx-auto" data-testid="pt-cta-button">
              <span>Agendar Sessão</span>
              <ArrowRight size={20} />
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
